import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { getPath } from "common/routing";
import { TranslationKeys } from "common/utils/i18n/fallbackEnTranslations";
import {
  ConfirmEmailErrorCode,
  ConfirmEmailMutation,
  useConfirmEmailMutation,
} from "../../../generated/graphql";
import { colors, spacing } from "../../../stitches.config";
import { useConfirmEmailParamParser } from "../../../utils/params";
import { AuthContext } from "../../Authorization/AuthContext";
import { CenterContent } from "../../Common/__styles__/Layout";
import { Button } from "../../Common/Button";
import FullPageLoadingScreen from "../../Common/FullPageLoadingScreen";
import { Section, Sections } from "../../Common/Layout";
import { Body, Title } from "../../Common/Typography";
import { GuestLocationState } from "../../Guest";
import Layout from "../../Guest/Layout";
import { Container as LoginContainer } from "../CommonLoginComponents/__styles__/FormWithFullPageBackground";

export const ConfirmEmailSignUpContent = ({
  data,
}: {
  data?: Maybe<ConfirmEmailMutation>;
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { publicUser } = useContext(AuthContext);

  const openLoginModal = !publicUser;

  let content: {
    titleKey: TranslationKeys;
    bodyKey: TranslationKeys;
    buttonTextKey?: TranslationKeys;
    buttonOnClick?: () => void;
  } = {
    titleKey: "something-went-wrong-header",
    bodyKey: "something-went-wrong-text",
    buttonTextKey: "common-resend-code",
    buttonOnClick: () => history.push(getPath("requestEmailConfirmation")),
  };

  if (data?.confirmEmail.ok && data?.confirmEmail.requiresApproval) {
    content = {
      titleKey: "account-verified-pending-approval-header",
      bodyKey: "account-verified-pending-approval-text",
    };
  } else if (data?.confirmEmail.ok) {
    content = {
      titleKey: "account-verified-header",
      bodyKey: "account-verified-text",
      buttonTextKey: publicUser ? "tab-explore" : "sign-in",
      buttonOnClick: () =>
        history.push(getPath("guestHome"), {
          openLoginModal,
        } satisfies GuestLocationState),
    };
  }

  if (
    data?.confirmEmail.error?.code ===
    ConfirmEmailErrorCode.CONFIRMATION_CODE_EXPIRED
  ) {
    content = {
      titleKey: "expired-link-header",
      bodyKey: "expired-link-text",
      buttonTextKey: "common-resend-code",
      buttonOnClick: () => history.push(getPath("requestEmailConfirmation")),
    };
  }

  const { titleKey, bodyKey, buttonTextKey, buttonOnClick } = content;

  return (
    <Sections>
      <Section>
        <Title type="semiBold" size="large">
          {t(titleKey)}
        </Title>
        <Body size="default" type="regular">
          {t(bodyKey)}
        </Body>
      </Section>
      {buttonTextKey && buttonOnClick && (
        <Button
          styleVariant="primary"
          size="medium"
          stretch
          onClick={buttonOnClick}
        >
          {t(buttonTextKey)}
        </Button>
      )}
    </Sections>
  );
};

export const ConfirmEmailSignup = () => {
  const token = useConfirmEmailParamParser();

  const [confirmEmail, { data, loading }] = useConfirmEmailMutation();

  useEffect(() => {
    if (token) {
      void confirmEmail({ variables: { token } });
    }
  }, [token, confirmEmail]);

  if (loading) return <FullPageLoadingScreen />;

  return (
    <Layout
      backgroundColor={colors.bgUiContainerInverted}
      footerFontColor="contentPrimaryInverted"
    >
      <CenterContent style={{ padding: spacing.l.value }}>
        <LoginContainer>
          <ConfirmEmailSignUpContent data={data} />
        </LoginContainer>
      </CenterContent>
    </Layout>
  );
};
